import { useCallback, useEffect, useState } from "react";
import { ScrollText, Search, Filter, Loader2, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import DataTable from "@/components/common/DataTable";
import { useCan } from "@/components/common/Can";
import Forbidden from "@/pages/Forbidden";
import { exportToExcel } from "@/lib/exports";

const ACTION_STYLES = {
    login:            "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]",
    logout:           "bg-muted text-muted-foreground",
    create:           "bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]",
    update:           "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
    update_permissions: "bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]",
    delete:           "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
    reset_password:   "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
    forgot_password_request: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]",
};

export default function AuditLog() {
    const canView = useCan("audit.view");
    const canExport = useCan("audit.export");

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState("");
    const [module, setModule] = useState("");
    const [action, setAction] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [modules, setModules] = useState([]);
    const [actions, setActions] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (search) params.search = search;
            if (module) params.module = module;
            if (action) params.action = action;
            if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
            if (dateTo) params.date_to = new Date(dateTo + "T23:59:59").toISOString();
            const { data } = await api.get("/audit-logs", { params });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, search, module, action, dateFrom, dateTo]);

    useEffect(() => {
        if (!canView) return;
        (async () => {
            try {
                const [m, a] = await Promise.all([
                    api.get("/audit-logs/distinct/modules"),
                    api.get("/audit-logs/distinct/actions"),
                ]);
                setModules(m.data.items || []);
                setActions(a.data.items || []);
            } catch (_) { /* ignore */ }
        })();
    }, [canView]);

    useEffect(() => { if (canView) load(); }, [load, canView]);

    if (!canView) return <Forbidden />;

    const clearFilters = () => {
        setSearch(""); setModule(""); setAction(""); setDateFrom(""); setDateTo(""); setPage(1);
    };

    const filtersActive = search || module || action || dateFrom || dateTo;

    const exportRows = () => rows.map((r) => ({
        timestamp: new Date(r.timestamp).toLocaleString("tr-TR"),
        user_name: r.user_name || "—",
        module: r.module || "—",
        action: r.action || "—",
        entity: [r.entity, r.entity_id].filter(Boolean).join(" #") || "—",
        ip: r.ip || "—",
    }));
    const exportColumns = [
        { header: "Tarih", key: "timestamp", width: 22 },
        { header: "Kullanıcı", key: "user_name", width: 24 },
        { header: "Modül", key: "module", width: 16 },
        { header: "Aksiyon", key: "action", width: 16 },
        { header: "Nesne", key: "entity", width: 26 },
        { header: "IP", key: "ip", width: 16 },
    ];

    const columns = [
        {
            key: "timestamp", header: tr.audit.columns.timestamp,
            render: (r) => (
                <div className="whitespace-nowrap">
                    <div className="text-[12.5px] font-mono font-semibold">{new Date(r.timestamp).toLocaleTimeString("tr-TR")}</div>
                    <div className="text-[10.5px] text-muted-foreground">{new Date(r.timestamp).toLocaleDateString("tr-TR")}</div>
                </div>
            ),
        },
        {
            key: "user", header: tr.audit.columns.user,
            render: (r) => <div className="text-[13px] font-semibold">{r.user_name || "—"}</div>,
        },
        {
            key: "module", header: tr.audit.columns.module,
            render: (r) => (
                <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase text-muted-foreground">
                    {r.module || "—"}
                </span>
            ),
        },
        {
            key: "action", header: tr.audit.columns.action,
            render: (r) => (
                <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold ${ACTION_STYLES[r.action] || "bg-muted text-muted-foreground"}`}>
                    {r.action}
                </span>
            ),
        },
        {
            key: "entity", header: tr.audit.columns.entity,
            render: (r) => (
                <div className="text-[12.5px] text-muted-foreground truncate max-w-[260px]">
                    {r.entity ? <span className="font-mono">{r.entity}</span> : "—"}
                    {r.entity_id && <span className="font-mono ml-1">#{r.entity_id.slice(0, 8)}</span>}
                </div>
            ),
        },
        {
            key: "ip", header: tr.audit.columns.ip,
            render: (r) => <div className="text-[12px] font-mono text-muted-foreground">{r.ip || "—"}</div>,
        },
    ];

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight">{tr.audit.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.audit.subtitle}</p>
                </div>
                {canExport && (
                    <button
                        onClick={() => exportToExcel({ filename: "denetim-kayitlari", sheetName: "Audit", columns: exportColumns, rows: exportRows() })}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold hover:bg-muted"
                    >
                        <FileSpreadsheet className="h-4 w-4" /> {tr.common.exportExcel}
                    </button>
                )}
            </div>

            <div className="card-elevated p-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="relative md:col-span-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Ara…"
                            className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
                            data-testid="audit-search"
                        />
                    </div>
                    <select value={module} onChange={(e) => { setModule(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="audit-module">
                        <option value="">{tr.audit.allModules}</option>
                        {modules.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="audit-action">
                        <option value="">{tr.audit.allActions}</option>
                        {actions.map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" placeholder={tr.audit.dateFrom} />
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40" placeholder={tr.audit.dateTo} />
                </div>
                {filtersActive && (
                    <div className="mt-3">
                        <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-[12px] hover:bg-muted">
                            <X className="h-3 w-3" /> {tr.common.clearFilters}
                        </button>
                    </div>
                )}
            </div>

            <DataTable
                columns={columns}
                rows={rows}
                loading={loading}
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
                sort="timestamp"
                order="desc"
                onSortChange={() => {}}
            />
        </div>
    );
}
